import React from 'react';
import { kgsToPounds, poundsToKgs } from '../../helpers/utils';
import './index.css';

/**
 * Unit-aware weight input — replaces the bare `<Input addonAfter='kg'>`
 * pattern that silently confused trainers with American clients who
 * happened to be metric (or vice versa).
 *
 * Display:
 *   ┌──────────────────────┬────────┐
 *   │ 151                  │ lb ▾   │   ← clickable pill toggles kg ↔ lb
 *   └──────────────────────┴────────┘
 *
 * Toggling the unit **converts the current value** so the trainer
 * sees the equivalent in the new unit rather than losing what they
 * typed. The owner of the data decides what to do with the
 * (value, unit) pair on save — typically:
 *
 *   • If the storage contract is "value in profile.isMetric's unit"
 *     (ProfileForm.currentWeight), the parent converts to the
 *     profile's unit before storing.
 *   • If the storage shape is `{kg, lb}` (BodyFatMeasurement.weight),
 *     the parent derives both sides from the (value, unit) pair.
 *
 * Either way the field hands the parent ground-truth about what unit
 * the value is in. No more silent unit mismatch.
 *
 * Props:
 *   value         — number | string — the displayed value (in `unit`)
 *   unit          — 'kg' | 'lb' — what the displayed value represents
 *   onChange      — (value: string, unit: 'kg' | 'lb') => void
 *   label         — string — shown above the input
 *   required      — boolean — adds a `*` to the label
 *   placeholder   — string
 *   id            — string — for label association
 *   className     — string — extra classes on the wrapper
 */
export class WeightField extends React.Component {
  static defaultProps = {
    unit: 'kg',
    required: false,
    placeholder: 'Enter weight',
  };

  handleValueChange = (e) => {
    const next = e.target.value;
    this.props.onChange(next, this.props.unit);
  };

  toggleUnit = () => {
    const { unit, value, onChange } = this.props;
    const nextUnit = unit === 'kg' ? 'lb' : 'kg';
    // Convert the current value to the new unit so the trainer
    // doesn't lose what they typed. Empty / non-numeric input passes
    // through untouched so we don't accidentally insert "0" when
    // they're mid-typing.
    const n = Number(value);
    let nextValue = value;
    if (Number.isFinite(n) && value !== '' && value != null) {
      const converted = nextUnit === 'lb' ? kgsToPounds(n) : poundsToKgs(n);
      // Round to 1 decimal to match the precision the rest of the
      // app stores at; trims trailing-zero noise like "151.0".
      const rounded = Math.round(Number(converted) * 10) / 10;
      nextValue = String(rounded);
    }
    onChange(nextValue, nextUnit);
  };

  render() {
    const { value, unit, label, required, placeholder, id, className } =
      this.props;
    return (
      <div className={`weight-field ${className || ''}`}>
        {label ? (
          <label className="weight-field-label" htmlFor={id}>
            {label}
            {required ? <span className="weight-field-required"> *</span> : null}
          </label>
        ) : null}
        <div className="weight-field-row">
          <input
            id={id}
            type="number"
            inputMode="decimal"
            step="0.1"
            className="weight-field-input"
            value={value ?? ''}
            onChange={this.handleValueChange}
            placeholder={placeholder}
          />
          <button
            type="button"
            className="weight-field-unit"
            onClick={this.toggleUnit}
            aria-label={`Switch to ${unit === 'kg' ? 'pounds' : 'kilograms'}`}
            title={`Switch to ${unit === 'kg' ? 'lb' : 'kg'} (value converts)`}
          >
            {unit}
          </button>
        </div>
      </div>
    );
  }
}

export default WeightField;
